/**
 * Report this account's WhatsApp restriction + send quota to the backend.
 *
 * ─── Why this is NOT bolted onto SessionStatusAutomaton ───
 *
 * The obvious home looked like the automaton's existing 5-minute session heartbeat, but that
 * heartbeat returns early without a `sessionId` — which every plain browser-extension user
 * lacks (only gateway sessions have a session doc). Riding it would have silently limited
 * the whole feature to gateway users. This reporter is deliberately session-independent: all
 * it needs is a connected WhatsApp and a logged-in Blueticks user.
 *
 * ─── What it reports ───
 *
 * The reachout-timelock restriction (why sends are being refused with ack 463) and the
 * new-chat message quota that precedes it. Both come from WA Web's own prefs cache via the
 * `waAccountRestrictionTool` engine tool; the backend stores them per WhatsApp number in
 * `account-health` and appends a history row on transitions.
 *
 * ─── The rule that matters ───
 *
 * A FAILED probe reports NOTHING. Reporting nulls after a failed read would clear a live
 * restriction, and the abandoned-message reaper would go back to telling a restricted
 * customer their sends failed because no engine was connected — the exact miscommunication
 * this exists to end.
 */

// Both the tool and the api helper are imported DYNAMICALLY inside probeAndReport, not at
// module scope. api-call-helper pulls in app.config, which reads `window.location` at import
// time — statically importing it here drags that into the module graph of everything that
// imports the automaton, and takes its test suites down with it. Same reason
// SessionStatusAutomaton dynamically imports CampaignThunks.
import { ContentMessageServiceActions } from './contentMessageServiceActions';
import { shouldProbeForRestriction } from './account-health-gate';
import { isWhatsappExtensionContext } from '@common/services/detect-context';
import { isExtensionContextAlive, readChromeRuntime } from '@common/services/orphan-guard';

/** Matches the Baileys pod's cadence. A restriction lasts days; probing harder buys nothing
 *  and costs a MEX round-trip per tick across the whole fleet. */
const REPORT_INTERVAL_MS = 15 * 60 * 1000;

/** WhatsApp populates its prefs cache during post-connect sync. Probing the instant we see
 *  `connected` reads an empty cache and reports a false all-clear. */
const FIRST_PROBE_DELAY_MS = 60 * 1000;

const log = (...args: unknown[]) => console.log('[account-health]', ...args);

export class AccountHealthReporter {
    private static instance: AccountHealthReporter | null = null;

    private timer: ReturnType<typeof setInterval> | null = null;
    private firstProbeTimer: ReturnType<typeof setTimeout> | null = null;
    private started = false;
    private probing = false;

    public static getInstance(): AccountHealthReporter {
        if (!AccountHealthReporter.instance) {
            AccountHealthReporter.instance = new AccountHealthReporter();
        }
        return AccountHealthReporter.instance;
    }

    /**
     * Arm the probe loop. Idempotent — a reconnect storm must not stack intervals, so the
     * first call wins until `stop()`.
     *
     * Called from TWO places on purpose, because neither alone is sufficient:
     *
     *  - content boot (wa-state-manager), which runs for every extension user; and
     *  - the automaton's connect edge, which arms it promptly once WhatsApp is up.
     *
     * The connect edge alone was the original design and it was WRONG: on a plain extension
     * profile the automaton receives `state: undefined` and falls through to its
     * "unhandled state" branch, so `isReconnectEdge` never becomes true and the reporter
     * never armed — for exactly the users it exists to cover. Caught on the ext-rig, where
     * the same profile also logs "Session ID not found, not updating".
     *
     * Arming before WhatsApp is ready is harmless: the probe reports NOTHING unless it gets
     * `status: 'ok'`, so an early tick is a no-op rather than a false all-clear.
     */
    public start(): void {
        if (this.started) return;
        // #764: the web app bundles this code but has no WhatsApp page to probe — every tick
        // there was a guaranteed 10s bridge timeout. Not marked started, so a later call on
        // the WA page still arms.
        if (!isWhatsappExtensionContext()) return;
        this.started = true;
        log(`armed — first probe in ${FIRST_PROBE_DELAY_MS / 1000}s, then every ${REPORT_INTERVAL_MS / 60000}min`);

        this.firstProbeTimer = setTimeout(() => { void this.probeAndReport(); }, FIRST_PROBE_DELAY_MS);
        this.timer = setInterval(() => { void this.probeAndReport(); }, REPORT_INTERVAL_MS);

        // `unref` where it exists (node/jest) so a background probe timer can never hold a
        // process open. In the browser these are plain numeric handles and the optional call
        // is a no-op. Without this, any test that drives the automaton past a connect edge
        // hangs Jest on an open handle — which is exactly what happened.
        (this.firstProbeTimer as unknown as { unref?: () => void })?.unref?.();
        (this.timer as unknown as { unref?: () => void })?.unref?.();
    }

    /** Kept for the automaton's connect edge — same idempotent arming. */
    public onConnected(): void {
        this.start();
    }

    public stop(): void {
        if (this.firstProbeTimer) { clearTimeout(this.firstProbeTimer); this.firstProbeTimer = null; }
        if (this.timer) { clearInterval(this.timer); this.timer = null; }
        this.started = false;
    }

    /**
     * One probe → report cycle. Never throws: this runs on a timer with no caller to handle
     * a rejection, and an unhandled one in the content script surfaces as a page error.
     */
    public async probeAndReport(): Promise<void> {
        // A slow probe must not overlap the next tick and double-report.
        if (this.probing) return;
        this.probing = true;
        try {
            // #764: an orphaned content script (the extension updated, this WhatsApp tab was
            // not reloaded) has no `chrome.runtime`, so the page bridge never answers and every
            // tick was a 10s `bridge_timeout` report. Nothing here can recover without a tab
            // reload, so stop the loop instead of probing a dead context forever.
            if (!isExtensionContextAlive(readChromeRuntime)) {
                log('extension context invalidated — stopping the probe loop');
                this.stop();
                return;
            }

            // GATE: never probe without a connected engine. On the standalone web app (no
            // linked engine) `waAccountRestrictionTool` round-trips to an engine-less backend
            // and fails every cycle — the phantom `/actions` 400; inside the extension it would
            // read an empty prefs cache. Skip cleanly; the next tick re-checks.
            if (!(await this.hasConnectedEngine())) {
                log('no connected engine — skipping probe');
                return;
            }

            const [{ waAccountRestrictionTool }, { apiCall }] = await Promise.all([
                import('../thunks/WhatsappThunks'),
                import('@extension/wa-communication-layer/api/api-call-helper'),
            ]);

            const probe = await waAccountRestrictionTool({ refresh: true });
            if (probe.success === false) {
                log('probe failed — reporting nothing', probe.error?.code);
                return;
            }
            const reading = probe.data;
            if (reading.status !== 'ok') {
                // 'unavailable' means we could not ask. Saying nothing leaves the last known
                // state standing, which is strictly better than asserting an all-clear.
                log('probe unavailable — reporting nothing');
                return;
            }

            const waAccountId = await this.resolveWaAccountId();
            if (!waAccountId) {
                // Without the number we cannot key the record, and a blank key would let two
                // linked numbers overwrite each other.
                log('no WhatsApp id yet — skipping');
                return;
            }

            await apiCall('post', '/actions?cmd=accountHealth.report', {
                waAccountId,
                restriction: reading.restriction,
                quota: reading.quota,
                reportedBy: 'extension',
            });

            if (reading.restriction?.isActive) {
                log(`RESTRICTED until ${reading.restriction.timeEnforcementEnds ?? 'unknown'} (${reading.restriction.enforcementType ?? 'no type'})`);
            } else if (reading.quota?.status && reading.quota.status !== 'NONE') {
                log(`quota ${reading.quota.status} — ${reading.quota.used ?? '?'}/${reading.quota.total ?? '?'}`);
            }
        } catch (error) {
            log('report failed (non-fatal)', (error as Error)?.message);
        } finally {
            this.probing = false;
        }
    }

    /**
     * True only when a WhatsApp engine is connected for this user. Reads the SAME connection
     * state the app header derives, via the context-agnostic store accessor (`getStateDirectly`
     * — the one NotifierService uses), so it is correct under BOTH the extension store and the
     * web-app store. Imported dynamically for the same module-graph reason the tool + api helper
     * are (see the file header). Never throws: an unreadable store fails CLOSED (no probe).
     */
    private async hasConnectedEngine(): Promise<boolean> {
        try {
            const [{ getStateDirectly }, { makeSelectConnectedEnginesState }] = await Promise.all([
                import('@common/state/state-provider'),
                import('@common-reducers/ConnectedEnginesSlice'),
            ]);
            const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
            const conn = makeSelectConnectedEnginesState(hostname)(getStateDirectly() as any);
            return shouldProbeForRestriction(conn);
        } catch (error) {
            log('connection state unreadable — skipping probe', (error as Error)?.message);
            return false;
        }
    }

    /** The WhatsApp number this reading belongs to. */
    private async resolveWaAccountId(): Promise<string> {
        try {
            const meUser = await ContentMessageServiceActions.getWaMeUser();
            return typeof meUser === 'string' ? meUser.trim() : '';
        } catch {
            return '';
        }
    }
}
